import { useEffect, useRef, useState, useCallback, MutableRefObject } from 'react';
import { Socket } from 'socket.io-client';

type ListenersType = {
  [subscriptionId: string]: {
    [eventName: string]: {
      [callbackId: string]: MutableRefObject<(data: unknown) => void>;
    };
  };
};

type HandlerMap = Map<string, (data: unknown) => void>;

/**
 * Manages socket event listeners registration and cleanup
 * Also manages subscriptions array (derived from listeners - tracks which rooms have listeners)
 * Returns listeners state, subscriptions array, and add/remove functions
 */
export function useSocketListeners(socket: Socket | null, socketReady: boolean) {
  const [listeners, setListeners] = useState<ListenersType>({});
  const [subscriptions, setSubscriptions] = useState<string[]>([]);
  // Ref to store event handler functions for proper cleanup (must match function references)
  const handlerMapRef = useRef<Map<string, HandlerMap>>(new Map());

  // Event listener management effect
  // Registers socket event listeners when listeners state changes
  // Uses named handler functions stored in a Map to ensure proper cleanup
  useEffect(() => {
    if (!socket || !socketReady) {
      return;
    }

    const currentHandlers = new Map<string, HandlerMap>();

    // Register all listeners and store handler references for cleanup
    // We must store the exact function references to remove them later
    for (const [room, events] of Object.entries(listeners)) {
      for (const [eventName, callbacks] of Object.entries(events)) {
        const eventKey = `<${room}>:${eventName}`;
        const roomHandlers = currentHandlers.get(eventKey) || new Map();
        currentHandlers.set(eventKey, roomHandlers);

        for (const [callbackId, callbackRef] of Object.entries(callbacks)) {
          // Create named handler function - this reference must be stored for cleanup
          // Using anonymous functions would prevent proper removal since socket.off()
          // requires the exact same function reference
          function handler(data: unknown) {
            callbackRef.current(data);
          }

          socket.on(eventKey, handler);
          roomHandlers.set(callbackId, handler);
        }
      }
    }

    // Store handlers map for cleanup function to access
    handlerMapRef.current = currentHandlers;

    return () => {
      // Remove all registered handlers using stored function references
      // This ensures we only remove handlers we registered, not ones from other components
      for (const [eventKey, roomHandlers] of currentHandlers.entries()) {
        for (const handler of roomHandlers.values()) {
          socket.off(eventKey, handler);
        }
      }
    };
  }, [socket, socketReady, listeners]);

  // Add a listener for a specific room/event combination
  // Also adds the room to subscriptions list (used for server-side room subscription)
  const addListener = useCallback(
    (room: string, eventName: string, callbackId: string, callback: MutableRefObject<(data: any) => void>) => {
      // Add room to subscriptions (deduplicated)
      setSubscriptions((prev) => [...new Set([...prev, room])]);
      // Add callback to listeners nested structure: listeners[room][eventName][callbackId]
      setListeners((prev) => {
        const newListeners = { ...prev };
        if (!newListeners[room]) {
          newListeners[room] = {};
        }
        if (!newListeners[room][eventName]) {
          newListeners[room][eventName] = {};
        }
        newListeners[room][eventName][callbackId] = callback;
        return newListeners;
      });
    },
    []
  );

  // Remove a listener and clean up empty nested structures
  // Also removes room from subscriptions if no listeners remain for that room
  const removeListener = useCallback((room: string, eventName: string, callbackId: string) => {
    setListeners((prev) => {
      const newListeners = { ...prev };
      if (!newListeners[room]) {
        return newListeners;
      }
      if (!newListeners[room][eventName]) {
        return newListeners;
      }
      // Remove the specific callback
      delete newListeners[room][eventName][callbackId];
      // Clean up empty eventName object if no callbacks remain
      if (Object.keys(newListeners[room][eventName]).length === 0) {
        delete newListeners[room][eventName];
      }
      // Clean up empty room object and remove from subscriptions if no events remain
      if (Object.keys(newListeners[room]).length === 0) {
        setSubscriptions((prev) => prev.filter((r) => r !== room));
        delete newListeners[room];
      }
      return newListeners;
    });
  }, []);

  return { listeners, subscriptions, addListener, removeListener };
}
